import * as React from "react";
import { PhotoBand } from "./PhotoBand";
import { SectionHeading } from "./SectionHeading";

export interface PageHeroProps {
  /**
   * Absolute path under `public/`. Provenance and the rules for adding to
   * `public/photos/` are in `docs/LEGAL-BLOCKERS.md` §5.
   */
  image: string;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  lead?: React.ReactNode;
  /**
   * The page's one small affordance, pinned to the band's bottom corner — the
   * parcel count on My farm, the refresh control on Operations. Out of the
   * heading's flow, so it cannot resize the band when it arrives late.
   */
  corner?: React.ReactNode;
  /**
   * The band's own call to action, centred under the lead — the log-in prompt
   * on a gated page. Distinct from `corner`, which is a pinned label that must
   * not affect the band's height. Use `outline-inverse` on anything with a
   * frame: the resting grey border of the default variant vanishes into a
   * photograph.
   */
  action?: React.ReactNode;
  className?: string;
}

/**
 * The title band of an interior page, set over one of the farm's own
 * photographs. A `PhotoBand` carrying an `h1`.
 *
 * It replaces the cream band rather than stacking on top of it: the band that
 * commit 7b64615 trimmed was eating the first screen, and a photograph is only
 * an improvement if the content underneath still starts above the fold. Hence
 * the height caps inherited from PhotoBand — ~240px on a phone, ~304px on a
 * desktop, which is what the cream band already occupied.
 *
 * The heading is centred over the photograph rather than tucked into a bottom
 * corner, which is how kuebefarm.lu sets its own page titles ("Our History"
 * over the sheep field). Centring is otherwise avoided on this site — see
 * SectionHeading — and a hero is the one place it is meant for.
 */
const PageHero = ({
  image,
  eyebrow,
  title,
  lead,
  corner,
  action,
  className,
}: PageHeroProps) => (
  <PhotoBand
    as="header"
    image={image}
    eager
    corner={corner}
    className={className}
  >
    <SectionHeading
      as="h1"
      align="center"
      tone="inverse"
      eyebrow={eyebrow}
      title={title}
      lead={lead}
      action={action}
    />
  </PhotoBand>
);

export { PageHero };
